import { useEffect, useMemo, useState } from "react";

import { IoCheckmark } from "react-icons/io5";
import { FiCheck, FiCopy, FiUser, FiXCircle } from "react-icons/fi";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { toast } from "react-toastify";
import apisecured from "../../api/apisecured";
import { useDispatch } from "react-redux";
import { setToast } from "../../State/toastSlice";

const Referral = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      dispatch(
        setToast({ message: `Copied ${code} to clipboard`, type: "success" }),
      );
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      dispatch(setToast({ message: "Failed to copy", type: "error" }));
    }
  };

  const formatDate = (dateStrOrIso) => {
    if (!dateStrOrIso) return "";
    const d = new Date(dateStrOrIso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchCodes = async () => {
      try {
        setLoading(true);

        const res = await apisecured.get("account/referral-stats");
        // Axios puts response body in res.data
        setData(res.data);
      } catch (e) {
        // Your interceptor already toasts, but this keeps your fallback data safe
        setData({ availableCount: 0, usedCount: 0, totalCount: 0, codes: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchCodes();
  }, []);

  const stats = useMemo(() => {
    return [
      {
        label: "Available",
        value: data?.availableCount ?? 0,
        color: "text-[#00FF8F]",
      },
      { label: "Used", value: data?.usedCount ?? 0, color: "text-yellow-400" },
      {
        label: "Total Codes",
        value: data?.totalCount ?? 0,
        color: "text-white",
      },
    ];
  }, [data]);

  const referralCodes = data?.codes ?? [];

  return (
    <div className="min-h-screen bg-[#030021] text-white selection:bg-[#00FF8F]/30  relative overflow-hidden !font-sans ">
      {/* Background Glows */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#00FF8F]/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-pink-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-[900px] w-full mx-auto relative z-10 px-[24px] py-[48px]">
        {/* Header */}
        <header className="mb-8 lg:mb-10">
          <h1 className="text-[28px] sm:text-[32px] font-bold mb-[8px] flex items-center gap-[12px] font-sans ">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-[28px] sm:w-[32px] h-[28px] sm:h-[32px] text-[#00FF8F]"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Referral Codes
          </h1>
          <p className="text-white/60 text-base sm:text-[16px] font-sans ">
            Invite other creators to join SPONSA. Share a code and they'll get
            instant creator access.
          </p>
        </header>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-[rgba(255,_255,_255,_0.03)] border-[1px] border-solid border-[rgba(255,255,255,0.08)] rounded-[16px] px-[24px] py-[20px] [transition:all_0.2s_ease] hover:bg-[rgba(255,_255,_255,_0.06)] hover:border-[rgba(255,_255,_255,_0.12)]  "
            >
              <div className="text-[13px] text-[rgba(255,_255,_255,_0.4)] uppercase tracking-[0.5px] mb-[16px] font-[500] font-sans">
                {stat.label}
              </div>
              <div
                className={`text-2xl sm:text-[28px] font-bold ${stat.color}`}
              >
                {loading ? "…" : stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Codes Table Section */}
        <section className="bg-[rgba(255,_255,_255,_0.03)] border-[1px] border-solid border-[rgba(255,255,255,0.08)] rounded-[20px]">
          <div className="flex justify-between items-center px-[24px] py-[20px] border-b border-b-solid border-b-[rgba(255,255,255,0.08)]">
            <h2 className="text-base sm:text-[18px] font-semibold">
              Your Codes
            </h2>
          </div>

          <div className="p-[8px]  ">
            {!loading && referralCodes.length === 0 && (
              <div className="p-6 text-white/60">No referral codes found.</div>
            )}

            {referralCodes.map((item) => (
              <div
                key={item.code}
                className="px-[20px] py-[16px] rounded-[12px] hover:bg-[rgba(255,255,255,0.06)] transition-colors group sm:grid sm:grid-cols-12 lg:gap-4 sm:items-center"
              >
                {/* Small screens */}
                <div className="sm:hidden flex justify-between items-start gap-3 mb-4">
                  <div className="flex flex-col gap-3 mb-4">
                    <div>
                      <span className="text-[#00FF8F] font-medium tracking-wider block">
                        {item.code}
                      </span>
                      <span className="text-[11px] text-white/30">
                        Created {formatDate(item.createdOn)}
                      </span>
                    </div>

                    <div>
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                          item.status === "available"
                            ? "bg-[#00FF8F]/10 text-[#00FF8F]"
                            : item.status === "used"
                              ? "bg-yellow-400/10 text-yellow-400"
                              : "bg-red-500/10 text-red-500"
                        }`}
                      >
                        {item.status === "available" ? (
                          <IoCheckmark size={14} />
                        ) : item.status === "used" ? (
                          <FiUser size={14} />
                        ) : (
                          <FiXCircle size={14} />
                        )}
                        {item.status.charAt(0).toUpperCase() +
                          item.status.slice(1)}
                      </span>
                    </div>

                    <div>
                      {item.usedBy ? (
                        <div className="flex items-center gap-3">
                          {item.usedBy.imageUrl ? (
                            <img
                              src={item.usedBy.imageUrl}
                              alt={item.usedBy.sponsaUserName}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-yellow-500 flex items-center justify-center text-xs font-bold text-[#030021]">
                              {(item.usedBy.sponsaUserName || "U")
                                .slice(0, 1)
                                .toUpperCase()}
                            </div>
                          )}

                          <div className="flex flex-col leading-tight">
                            <span className="text-sm font-medium">
                              {item.usedBy.sponsaUserName}
                            </span>
                            <span className="text-[10px] text-white/30">
                              {formatDate(item.usedOn)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-white/20">— Not used yet</span>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      disabled={item.status !== "available"}
                      onClick={() => copyToClipboard(item.code)}
                      className={`inline-flex items-center justify-center w-[40px] h-[40px] bg-transparent border-[1px] border-solid border-[rgba(255,255,255,0.08)] rounded-[10px] text-[rgba(255,_255,_255,_0.6)] cursor-pointer [transition:all_0.2s_ease] ${
                        item.status !== "available"
                          ? "opacity-30 cursor-not-allowed"
                          : "hover:bg-[rgba(0,_255,_143,_0.1)] hover:border-[#00FF8F] hover:text-[#00FF8F]"
                      }`}
                    >
                      {copiedCode === item.code ? (
                        <FiCheck size={20} />
                      ) : (
                        <FiCopy size={20} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Large screens */}
                <>
                  <div className="hidden sm:flex flex-col col-span-6">
                    <span className="text-[#00FF8F] text-[16px] font-medium tracking-wider font-mono mb-1 ">
                      {item.code}
                    </span>
                    <span className="text-[13px] text-[rgba(255,255,255,0.4)]">
                      Created {formatDate(item.createdOn)}
                    </span>
                  </div>

                  <div className="hidden sm:block col-span-2 ml-auto ">
                    <span
                      className={`inline-flex items-center gap-[6px] px-[12px] py-[6px] rounded-[20px] text-[13px] font-medium ${
                        item.status === "available"
                          ? "bg-[#00FF8F]/10 text-[#00FF8F]"
                          : item.status === "used"
                            ? "bg-yellow-400/10 text-yellow-400"
                            : "bg-red-500/10 text-red-500"
                      }`}
                    >
                      {item.status === "available" ? (
                        <IoCheckmark size={14} />
                      ) : item.status === "used" ? (
                        <FiUser size={14} />
                      ) : (
                        <FiXCircle size={14} />
                      )}
                      {item.status.charAt(0).toUpperCase() +
                        item.status.slice(1)}
                    </span>
                  </div>

                  <div className="hidden sm:flex items-center gap-3 min-w-[150px] col-span-3 pl-1 ">
                    {item.usedBy ? (
                      <>
                        {item.usedBy.imageUrl ? (
                          <img
                            src={item.usedBy.imageUrl}
                            alt={item.usedBy.sponsaUserName}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-yellow-500 flex items-center justify-center text-xs font-bold text-[#030021]">
                            {(item.usedBy.sponsaUserName || "U")
                              .slice(0, 1)
                              .toUpperCase()}
                          </div>
                        )}

                        <div className="flex flex-col ">
                          <span className="text-[14px] font-medium text-[white] ">
                            {item.usedBy.sponsaUserName}
                          </span>
                          <span className="text-[12px] text-[rgba(255,_255,_255,_0.4)]">
                            {formatDate(item.usedOn)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <span className="text-white/20">—</span>
                    )}
                  </div>

                  <div className="hidden sm:flex justify-end col-span-1">
                    <button
                      disabled={item.status !== "available"}
                      onClick={() => copyToClipboard(item.code)}
                      className={`inline-flex items-center justify-center w-[40px] h-[40px] bg-transparent border-[1px] border-solid border-[rgba(255,255,255,0.08)] rounded-[10px] text-[rgba(255,_255,_255,_0.6)] cursor-pointer [transition:all_0.2s_ease] ${
                        item.status !== "available"
                          ? "opacity-30 cursor-not-allowed"
                          : "hover:bg-[rgba(0,_255,_143,_0.1)] hover:border-[#00FF8F] hover:text-[#00FF8F]"
                      }`}
                    >
                      {copiedCode === item.code ? (
                        <FiCheck size={18} />
                      ) : (
                        <FiCopy size={18} />
                      )}
                    </button>
                  </div>
                </>
              </div>
            ))}
          </div>
        </section>

        {/* Info Banner */}
        <div className="flex items-start gap-[12px] bg-[rgba(0,_255,_143,_0.1)] border-[1px] border-solid border-[rgba(0,255,143,0.2)] rounded-[12px] px-[20px] py-[16px] mt-[24px]">
          <IoIosInformationCircleOutline className="text-[#00FF8F] w-[20px] h-[20px] shrink-0 mt-0.5" />
          <p className="text-[14px] text-[rgba(255,_255,_255,_0.6)] leading-relaxed">
            When someone uses your code, they'll be set up as a{" "}
            <strong className="text-[#00FF8F]">Creator</strong> instantly - no
            waiting for approval! Each code can only be used once.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Referral;
